import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MessageSquare, UserPlus } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import WorkspaceTeamChatBlock from "@/components/equipo/WorkspaceTeamChatBlock";
import { EQUIPO_CHAT_PATH } from "@/modules/equipo/equipoRoutes";
import {
  buildPanelGeneralTabPath,
  PANEL_GENERAL_TABS,
} from "@/modules/panelGeneralTabs";
import {
  buildCommonToolReturnState,
} from "@/lib/equipoNavigation";
import { CONFIGURACION_DESPACHO_PATH } from "@/modules/config/configuracionRoutes";
import {
  MSG_LICENSE_USER_LIMIT,
  computeUserSlotStats,
  canUseBasicTeamFeatures,
  isWorkspaceUserActive,
} from "@/lib/workspaceTeamLimits";
import { canManageWorkspaceTeam } from "@/lib/auxiliaryRoleGuide";
import { useEntitlements } from "@/modules/useEntitlements";

/**
 * Contenido del chat interno general — usable embebido (Panel General) o en página completa.
 */
export default function ChatInternoEquipoPanel({
  embedded = false,
  inSplitCard = false,
  onOpenUsersManage = null,
}) {
  const { user } = useAuth();
  const { systemInfo, loading: infoLoading } = useEntitlements();
  const returnOrigin = embedded ? "/" : "/equipo/chat";
  const usuariosNavState = buildCommonToolReturnState(returnOrigin, {
    returnLabel: embedded ? "Volver al Panel General" : "Volver al chat interno",
    sourceModule: embedded ? "panel_general" : "equipo_chat",
  });
  const usuariosPath = buildPanelGeneralTabPath(PANEL_GENERAL_TABS.EQUIPO, returnOrigin);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const teamFeaturesReady = canUseBasicTeamFeatures(systemInfo);

  const canManageTeam = canManageWorkspaceTeam(user);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(Array.isArray(data) ? data : []);
    } catch {
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const activeUsers = useMemo(
    () => (users || []).filter(isWorkspaceUserActive),
    [users],
  );
  const activeCount = activeUsers.length;
  const slotStats = useMemo(() => computeUserSlotStats(systemInfo, users), [systemInfo, users]);
  const teamFeatures = canUseBasicTeamFeatures(systemInfo);
  const onlyOwner = activeCount <= 1;

  const panelLoading = infoLoading || loadingUsers;

  const wrapClass = embedded && !inSplitCard
    ? "rounded-md border border-slate-200 bg-slate-50/80 p-4 space-y-3"
    : embedded
      ? "space-y-3"
      : "space-y-5";

  return (
    <div className={wrapClass} data-testid={embedded ? "chat-interno-panel-embedded" : "chat-interno-panel-full"}>
      {panelLoading ? (
        <div className="py-6 text-center text-sm text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        </div>
      ) : null}

      {!panelLoading && !teamFeatures ? (
        <div className="rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
          Su licencia actual permite un solo usuario. Solicite ampliación para habilitar chat interno con auxiliares.
          {canManageTeam ? (
            <Button asChild variant="outline" size="sm" className="mt-2 h-8 text-xs block">
              <Link to={`${CONFIGURACION_DESPACHO_PATH}?solicitar_licencia=1`}>Solicitar ampliación</Link>
            </Button>
          ) : null}
        </div>
      ) : null}

      {!panelLoading && teamFeatures && onlyOwner ? (
        <div
          className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-950"
          data-testid="chat-interno-solo-titular"
        >
          <p className="m-0 flex items-start gap-2">
            <MessageSquare className="w-4 h-4 shrink-0 mt-0.5" />
            Agrega un auxiliar para comenzar a usar el chat interno del equipo.
          </p>
          {canManageTeam ? (
            <div className="flex flex-wrap gap-2 mt-3">
              {onOpenUsersManage ? (
                <Button
                  type="button"
                  size="sm"
                  className="h-8 bg-[#0F2D5C] hover:bg-[#0a2247]"
                  onClick={onOpenUsersManage}
                >
                  <UserPlus className="w-3.5 h-3.5 mr-1" />
                  Administrar usuarios
                </Button>
              ) : (
                <Button asChild size="sm" className="h-8 bg-[#0F2D5C] hover:bg-[#0a2247]">
                  <Link to={usuariosPath} state={usuariosNavState}>
                    <UserPlus className="w-3.5 h-3.5 mr-1" />
                    Administrar usuarios
                  </Link>
                </Button>
              )}
              {slotStats.atLimit ? (
                <Button asChild variant="outline" size="sm" className="h-8 text-xs">
                  <Link to={`${CONFIGURACION_DESPACHO_PATH}?solicitar_licencia=1`}>Solicitar más auxiliares</Link>
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {!panelLoading && teamFeatures && !onlyOwner ? (
        <WorkspaceTeamChatBlock embedded />
      ) : null}

      {slotStats.atLimit && canManageTeam && !onlyOwner && teamFeatures ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
          {MSG_LICENSE_USER_LIMIT}
          <Button asChild variant="link" className="h-auto p-0 ml-1 text-xs text-amber-950 underline">
            <Link to={`${CONFIGURACION_DESPACHO_PATH}?solicitar_licencia=1`}>Solicitar más auxiliares</Link>
          </Button>
        </div>
      ) : null}
    </div>
  );
}
